import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DentistHomeProps } from './DentistHome.types';
import MonthView from '../../../components/DentistCalendar/MonthView';
import JobBoardPolicyModal from './components/JobBoardPolicyModal/JobBoardPolicyModal';
import PhotoAnnounceModal from '../../../components/PhotoAnnounceModal/PhotoAnnounceModal';
import Api from '../../../api';
import PostShiftModal from './components/PostShiftModal';
import JobSelectionModal from './components/JobSelectionModal';
import LocationSelectorModal, { DentistLocation } from './components/LocationSelectorModal';
import OfficeInfoConfirmModal, { OfficeLocationInfo } from './components/OfficeInfoConfirmModal/OfficeInfoConfirmModal';
import TermsModal from './components/TermsModal/TermsModal';
import { ChatbaseWidget, openChatbaseWidget, requestChatbaseSupport, type ChatbaseUserContext } from '../../../components/ChatWidget';
import { useCalendarData } from '../../../hooks/dentist/useCalendarData';
import { getUnviewedApplicationsCount } from '../../../api/dentist/calendarApi';
import { ProcessedJob } from '../../../types/dentist/calendar';
import FixedLoader from '../../../components/FixedLoader';
import DentistNavbar from '../../../components/DentistNavbar';
import WaveHandIcon from '../../../components/WaveHandIcon/WaveHandIcon';
import styles from './DentistHome.module.scss';
import { isJobStartDateEligibleForFeedbackSurveyPopup } from '../../../utils/surveyPopupEligibility';
import { getDentistHomeLocationSelectionAction } from './locationSelectionPolicy';

type ChatbaseRole = NonNullable<ChatbaseUserContext['metadata']>['role'];

// Module-level guard: prevents the survey navigation from firing more than once per session
// even if DentistHome remounts (e.g. after FeedbackPage navigates back).
let _surveyFlowStarted = false;
const SURVEY_RESHOW_MS = 24 * 60 * 60 * 1000;
const isFlagSet = (value: boolean | number | string | null | undefined) => value === true || value === 1 || value === '1';

const getDentistSurveyDisplayTime = (job: any) => {
  const rawValue = job?.shifts?.[0]?.shiftDate
    || job?.startDate;
  const time = rawValue ? new Date(rawValue).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

export const hasCompletedDentistSurvey = (job: any) => {
  const rating = Number(job?.hygienistRating);
  const hasRating = Number.isFinite(rating) && rating > 0;
  return isFlagSet(job?.feedbackSubmitted) || isFlagSet(job?.surveyDismissed) || hasRating;
};

export const isDentistSurveyEligible = (job: any) => {
  const skipCount = Number(job?.dentistSurveySkipCount || 0);
  if (skipCount >= 2) return false;
  if (!skipCount) return true;

  const skippedAt = job?.dentistSurveySkippedAt ? new Date(job.dentistSurveySkippedAt).getTime() : NaN;
  return Number.isFinite(skippedAt) && (Date.now() - skippedAt >= SURVEY_RESHOW_MS);
};

export const buildDentistHomeSurveyJobs = (rawJobs: any[] = []) => {
  const candidates = (rawJobs || []).filter((job: any) => {
    const hasHyg = !!job.hygienistId;
    const isCompleted = Number(job.status) === 4; // COMPLETED
    return hasHyg && isCompleted && !hasCompletedDentistSurvey(job) && isDentistSurveyEligible(job)
      && isJobStartDateEligibleForFeedbackSurveyPopup(job.startDate);
  }).sort((a: any, b: any) => getDentistSurveyDisplayTime(a) - getDentistSurveyDisplayTime(b));

  return candidates
    .filter((job: any) => job.hygienist?.firstName)
    .map((job: any) => ({
      job,
      hygienist: {
        id: job.hygienistId,
        firstName: job.hygienist.firstName,
        lastName: job.hygienist.lastName,
      },
      locationId: job.locationId,
    }));
};

export const getDentistHomeEmptyDateAction = (date: Date, jobs: ProcessedJob[]) => {
  const isPastDate = date < new Date(new Date().setHours(0, 0, 0, 0));
  const visibleJobs = isPastDate
    ? jobs.filter((job) => {
      const status = job.status as number;
      return status !== 5 && status !== 6;
    })
    : jobs;

  if (visibleJobs.length > 0) {
    return { action: 'jobs', jobs: visibleJobs } as const;
  }

  return { action: isPastDate ? 'none' : 'postShift', jobs: visibleJobs } as const;
};

const DentistHome: React.FC<DentistHomeProps> = () => {
  const history = useHistory();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [newApplicationsCount, setNewApplicationsCount] = useState(0);
  const [isPostShiftModalOpen, setIsPostShiftModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isJobSelectionModalOpen, setIsJobSelectionModalOpen] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<ProcessedJob[]>([]);
  const [isLocationSelectorOpen, setIsLocationSelectorOpen] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<DentistLocation[]>([]);
  const [jobsPerLocationCount, setJobsPerLocationCount] = useState<Record<number, number>>({});
  const [isOfficeInfoOpen, setIsOfficeInfoOpen] = useState(false);
  const [pendingOfficeLocation, setPendingOfficeLocation] = useState<OfficeLocationInfo | null>(null);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<{ open: boolean; photoUrl?: string; linkUrl?: string } | null>(null);
  // T2-6085: office Photo Announce is per-location — track the active location so
  // an id-list-targeted image appears/disappears as the office switches offices.
  const [officeLocations, setOfficeLocations] = useState<Array<{ id: number; practiceName?: string; city?: string }>>([]);
  const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
  const [chatbaseReady, setChatbaseReady] = useState(false);
  const [chatbaseError, setChatbaseError] = useState(false);
  const [chatbaseUserContext, setChatbaseUserContext] = useState<ChatbaseUserContext | null>(null);

  // Fetch calendar job data
  const { dateJobsMap, isLoading, error, getJobsForDate, rawJobs } = useCalendarData();

  // Load badge count on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const count = await getUnviewedApplicationsCount();
        if (mounted) setNewApplicationsCount(count);
      } catch (e) {
        // noop
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    try {
      const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
      if (!currentUser) return;
      let role: ChatbaseRole = 'dentist';
      if ((currentUser as any)?.isAdmin) {
        role = 'admin';
      } else if (!currentUser?.dentist) {
        role = 'member';
      }
      const displayName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
      setChatbaseUserContext({
        userId: currentUser?.id ? String(currentUser.id) : undefined,
        metadata: {
          name: displayName || undefined,
          email: currentUser?.email,
          role,
          region: currentUser?.dentist?.province || currentUser?.dentist?.state || undefined,
        },
      });
    } catch (error) {
      console.warn('Unable to derive Chatbase user context', error);
    }
  }, []);

  // Terms of Service modal logic (parity with legacy)
  useEffect(() => {
    try {
      const ts: any = (window as any).TempStars;
      const user = ts?.User?.getCurrentUser?.();
      const dentist = user?.dentist;
      const termsUpdated = ts?.TERMS_UPDATE_DATE;
      if (dentist && termsUpdated) {
        const seen = dentist.newTermsSeenOn ? new Date(dentist.newTermsSeenOn) : null;
        const mustSee = new Date(termsUpdated);
        if (!seen || seen < mustSee) {
          setIsTermsOpen(true);
        }
      }
      // T2-6085: office Photo Announce is delivered per-location via
      // Api.getOfficePhotoAnnounce (see the location effects below), not from the
      // cached account-level dentist.photoAnnounceUrl, so id-list targeting can
      // vary by the currently-selected location.

      // T2-6044: Legacy promotional Survey/SurveyAnswer triggers are intentionally
      // disabled. The legacy `survey` page (writes to Survey/SurveyAnswers tables)
      // is not surfaced to dental offices anymore. Data tables remain in place
      // for future repurposing — only the popup/CTA + navigation are hidden.
    } catch (e) {
      // ignore
    }
  }, []);

  // T2-6085: load the office's locations and default the active location.
  useEffect(() => {
    try {
      const ts: any = (window as any).TempStars;
      const user = ts?.User?.getCurrentUser?.();
      const locs = Array.isArray(user?.locations) ? user.locations : [];
      const mapped = locs
        .filter((l: any) => l && l.id)
        .map((l: any) => ({ id: l.id, practiceName: l.practiceName, city: l.city }));
      setOfficeLocations(mapped);
      if (mapped.length > 0) {
        setActiveLocationId((prev) => (prev && mapped.some((l: any) => l.id === prev) ? prev : mapped[0].id));
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // T2-6085: fetch the Photo Announce image for the active location. Switching
  // location re-evaluates visibility (id-list targeting + per-location 'seen').
  useEffect(() => {
    if (!activeLocationId) return undefined;
    let mounted = true;
    (async () => {
      try {
        const res = await Api.getOfficePhotoAnnounce(activeLocationId);
        if (!mounted) return;
        if (res && res.image && res.image.photoUrl) {
          setAnnouncement({
            open: true,
            photoUrl: res.image.photoUrl,
            linkUrl: res.image.announceUrl || undefined,
          });
        } else {
          setAnnouncement(null);
        }
      } catch (e) {
        // non-fatal: just don't show an announcement
      }
    })();
    return () => { mounted = false; };
  }, [activeLocationId]);

  // Completed-shift feedback survey flow (legacy parity)
  useEffect(() => {
    // Module-level guard: prevents re-triggering after FeedbackPage navigates back to this page
    if (_surveyFlowStarted) return;

    try {
      const surveyJobs = buildDentistHomeSurveyJobs(rawJobs || []);

      if (!surveyJobs.length) return;

      // Mark started before navigating so any remount doesn't re-trigger
      _surveyFlowStarted = true;
      const first = surveyJobs[0];
      history.push({
        pathname: 'feedbackPage',
        state: {
          ...first,
          surveyJobs,
          currentSurveyIndex: 0,
        },
      });
    } catch (e) {
      // noop
    }
  }, [rawJobs, history]);

  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  const computeJobsPerLocationForDate = useCallback((date: Date | null): Record<number, number> => {
    if (!date) return {};
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const key = `${year}-${month}-${day}`;
    const jobs = getJobsForDate(key) || [];
    const counts: Record<number, number> = {};
    jobs.forEach((job) => {
      const lid = job.locationId;
      counts[lid] = (counts[lid] || 0) + 1;
    });
    return counts;
  }, [getJobsForDate]);

  const handleLocationSelected = useCallback((location: DentistLocation) => {
    const selectionAction = getDentistHomeLocationSelectionAction(
      location,
      selectedDate,
      jobsPerLocationCount,
    );

    if (selectionAction.action === 'blocked') {
      window.alert(selectionAction.message);
      return;
    }

    if (selectionAction.action === 'confirmOfficeInfo') {
      setPendingOfficeLocation({
        id: location.id,
        address: location.address,
        primaryContact: location.primaryContact,
        parking: location.parking,
        avgApptTime: location.avgApptTime,
        radiography: location.radiography,
        ultrasonic: location.ultrasonic,
        charting: location.charting,
        software: location.software
      });
      setIsOfficeInfoOpen(true);
      return;
    }

    const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : undefined;
    (window as any).TempStars?.App?.goReact('postJob', {
      ...(dateStr ? { date: dateStr } : {}),
      location: location.id
    });
    setIsLocationSelectorOpen(false);
  }, [selectedDate, jobsPerLocationCount]);

  const startPostFlow = useCallback((date: Date | null) => {
    try {
      const ts: any = (window as any).TempStars;
      const dateStr = date ? date.toDateString() : undefined;
      // Navigate directly to PostJobPage, bypassing location selector modal
      ts?.App?.goReact('postJob', dateStr ? { date: dateStr } : undefined);
      setIsPostShiftModalOpen(false);
    } catch (e) {
      const dateStr = date ? date.toDateString() : undefined;
      (window as any).TempStars?.App?.goReact('postJob', dateStr ? { date: dateStr } : undefined);
      setIsPostShiftModalOpen(false);
    }
  }, []);

  const handleGetTemp = useCallback(() => {
    startPostFlow(null);
  }, [startPostFlow]);

  const handleHireStaff = useCallback(async () => {
    const currentUser = (window as any).TempStars?.User?.getCurrentUser?.();
    const accepted = currentUser?.dentist?.isAcceptedJobBoardPolicy;
    if (accepted) {
      (window as any).TempStars?.App?.goReact('jobBoard');
      return;
    }
    setIsPolicyModalOpen(true);
  }, []);

  const handleChatbaseLoad = useCallback(() => {
    setChatbaseReady(true);
    setChatbaseError(false);
  }, []);

  const handleChatbaseError = useCallback((error: Error) => {
    console.error('Chatbase widget failed to load', error);
    setChatbaseError(true);
  }, []);

  const openContactSupport = useCallback(() => {
    requestChatbaseSupport();
    (window as any).TempStars?.App?.goReact('dentistContactSupport');
  }, []);

  const handleHelpClick = useCallback(() => {
    if (!chatbaseError && openChatbaseWidget()) {
      return;
    }
    if (chatbaseError) {
      alert('AI assistant is temporarily unavailable. Redirecting to support center.');
    } else if (!chatbaseReady) {
      alert('AI assistant is still loading. Redirecting to support center.');
    }
    openContactSupport();
  }, [openContactSupport, chatbaseError, chatbaseReady]);

  const handleDateClick = useCallback((date: Date) => {
    const unifiedEnabled = !!(window as any)?.TempStars?.FeatureFlags?.unifiedPostedShiftEnabled;
    // Format date to match the dateKey format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    
    const allJobs = getJobsForDate(dateKey);
    
    // Filter out expired jobs (status 5 or 6) for past dates - they shouldn't be interactive
    const dateAction = getDentistHomeEmptyDateAction(date, allJobs);
    const jobs = dateAction.jobs;
    
    if (jobs.length > 0) {
      // Check if all jobs are posted (status 1) without offers
      const postedJobsWithoutOffers = jobs.filter(job => 
        job.status === 1 && // JOB_STATUS.POSTED (now 1, not 0)
        job.effectiveStatus === 1 && // Still posted after processing
        (!job.hasPartialOffers || (job.PartialOffers?.length === 0 && job.partialOffers?.length === 0))
      );
      
      // Check if this is a past date - we don't navigate for past dates
      const isPast = date < new Date(new Date().setHours(0,0,0,0));

      // If exactly one job on the date, navigate directly based on status (including past dates)
      if (jobs.length === 1) {
        const job = jobs[0];
        const params = { id: job.id, jobCount: 1 };
        // Unified routing for 1/2/5 → jobPosted when enabled
        if (unifiedEnabled) {
          if (job.status === 3) {
            history.push({ pathname: 'dentistJobConfirmed', state: params });
            return;
          }
          if (job.status === 4) {
            history.push({ pathname: 'jobCompleted', state: { jobId: job.id, jobCount: 1 } });
            return;
          }
          // POSTED, CUSTOM/PARTIAL, EXPIRED/OFFER_EXPIRED → unified page
          history.push({ pathname: 'jobPosted', state: params });
          return;
        } else {
          // Legacy routing
          switch (job.status) {
            case 1: // POSTED
              if (job.hasRemovedOffers && !job.hasActiveOffers) {
                history.push({ pathname: 'dentistCustomOffers', state: params });
              } else {
                history.push({ pathname: 'jobPosted', state: params });
              }
              return;
            case 2: // CUSTOM/PARTIAL
              history.push({ pathname: 'dentistCustomOffers', state: params });
              return;
            case 3: // BOOKED/CONFIRMED
              history.push({ pathname: 'dentistJobConfirmed', state: params });
              return;
            case 4: // COMPLETED
              history.push({ pathname: 'jobCompleted', state: { jobId: job.id, jobCount: 1 } });
              return;
            case 5: // EXPIRED/OFFER_EXPIRED
              history.push({ pathname: 'dentistCustomOffers', state: params });
              return;
            default:
              break;
          }
        }
      }

      if (!unifiedEnabled && postedJobsWithoutOffers.length === jobs.length) {
        // All jobs on this date are posted without offers
        if (jobs.length === 1) {
          // Single posted job - navigate directly to JobPostedPage
          const job = jobs[0];
          const params = { id: job.id, jobCount: 1 };
          if (job.hasRemovedOffers && !job.hasActiveOffers) {
            history.push({ pathname: 'dentistCustomOffers', state: params });
          } else {
            history.push({ pathname: 'jobPosted', state: params });
          }
          return; // Exit early to prevent modal
        } else {
          // Multiple posted jobs - show JobSelectionModal
          // (logging removed)
          setSelectedDate(date);
          setSelectedJobs(jobs);
          setIsJobSelectionModalOpen(true);
        }
      } else {
        // Mixed statuses or any jobs – show selection modal (also for past dates)
        if (jobs.length > 0) {
          // (logging removed)
          setSelectedDate(date);
          setSelectedJobs(jobs);
          setIsJobSelectionModalOpen(true);
        }
      }
    } else {
      // No jobs on this date (or only expired jobs on past dates)
      if (dateAction.action === 'none') {
        // Past date with no active jobs - don't allow any action
        return;
      }
      setSelectedDate(date);
      setIsPostShiftModalOpen(true);
    }
  }, [getJobsForDate, history]);

  const handlePostShiftConfirm = useCallback(() => {
    if (selectedDate) {
      startPostFlow(selectedDate);
    }
  }, [selectedDate, startPostFlow]);

  const handlePostShiftCancel = useCallback(() => {
    setIsPostShiftModalOpen(false);
    setSelectedDate(null);
  }, []);

  const handleJobSelectionModalClose = useCallback(() => {
    setIsJobSelectionModalOpen(false);
    setSelectedJobs([]);
  }, []);

  const handlePostNewShiftFromModal = useCallback(() => {
    setIsJobSelectionModalOpen(false);
    setIsPostShiftModalOpen(true);
  }, []);

  const handleOfficeInfoConfirm = useCallback(async () => {
    try {
      const ts: any = (window as any).TempStars;
      if (pendingOfficeLocation) {
        await ts?.Api?.updateLocation?.(Number(pendingOfficeLocation.id), { isUpdated: 1 });
      }
    } catch (e) {
      // ignore
    }
    setIsOfficeInfoOpen(false);
    if (pendingOfficeLocation) {
      handleLocationSelected({
        ...(pendingOfficeLocation as any),
        isUpdated: 1
      } as DentistLocation);
    }
  }, [pendingOfficeLocation, handleLocationSelected]);

  const handleOfficeInfoChange = useCallback(() => {
    setIsOfficeInfoOpen(false);
    if (pendingOfficeLocation) {
      history.push({ pathname: 'locationDetails', state: { location: pendingOfficeLocation } });
    }
  }, [pendingOfficeLocation, history]);

  const handleMonthChange = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  useEffect(() => {
    try {
      const ts: any = (window as any).TempStars;
      // Update side menu links to React routes (parity update)
      ts?.App && (ts.App.goInvoices = () => (window as any).TempStars?.App?.goReact('dentistInvoices'));
      ts?.App && (ts.App.goLocations = () => (window as any).TempStars?.App?.goReact('dentistLocations'));
    } catch (e) {
      // ignore bridge errors
    }
  }, []);

  // Get current user info
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const dentist = currentUser?.dentist;
  const userEmail = currentUser?.email || '';
  const practiceName = dentist?.practiceName || userEmail || 'Loading...';
  const city = dentist?.city || '';
  const province = dentist?.province || '';
  const firstName = currentUser?.firstName || '';
  const lastName = currentUser?.lastName || '';
  const photoUrl = currentUser?.photoUrl || dentist?.photoUrl || undefined;

  return (
    <>
    <ChatbaseWidget
      userContext={chatbaseUserContext}
      onLoad={handleChatbaseLoad}
      onError={handleChatbaseError}
      onContactSupport={openContactSupport}
    />
    <div className={styles.dentistHomePage}>
      <DentistNavbar
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        onGetTempClick={handleGetTemp}
        onHireStaffClick={handleHireStaff}
      />

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Welcome Section */}
        <section className={styles.welcomeSection}>
          <div className={styles.welcomeHeader}>
            <h2 className={styles.welcomeText}>
              Welcome back, {practiceName} <WaveHandIcon className={styles.waveEmoji} />
            </h2>
        </div>
          <p className={styles.instructions}>
            Tap a date on the calendar to find a temp or Get a Temp!
          </p>
        </section>

        {/* Hire Staff Section */}
        <section className={styles.hireStaffSection}>
          <div className={styles.hireStaffContainer}>
            <div className={styles.hireStaffLeft}>
              <div className={styles.hireStaffTextContainer}>
                <div className={styles.hireStaffTitle}>Looking to hire?</div>
                <div className={styles.hireStaffSubtitle}>Post permanent or contract positions!</div>
              </div>
              <button 
                className={styles.hireStaffButton}
                onClick={handleHireStaff}
              >
                Hire Staff
                {(newApplicationsCount ?? 0) > 0 && (
                  <span className={styles.hireStaffBadge} aria-label={`${newApplicationsCount} new applicants`}>
                    {newApplicationsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Calendar Section */}
        <section className={styles.calendarSection}>
        <div className={styles.calendarContainer}>
          {error && (
              <div className={styles.errorMessage}>{error}</div>
            )}
            
            <MonthView
              currentDate={currentDate}
              onDateClick={handleDateClick}
              onMonthChange={handleMonthChange}
              dateJobsMap={dateJobsMap}
            />
            
            {/* Legend */}
            <div className={styles.legendSection}>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.posted}`}></span>
                Available Shifts
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.booked}`}></span>
                Booked
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.active}`}></span>
                Active Offer
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.expired}`}></span>
                Expired
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.completed}`}></span>
                Completed
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Post Shift Modal */}
      <PostShiftModal
        isOpen={isPostShiftModalOpen}
        selectedDate={selectedDate}
        onConfirm={handlePostShiftConfirm}
        onCancel={handlePostShiftCancel}
      />

      {/* Job Selection Modal */}
      <JobSelectionModal
        isOpen={isJobSelectionModalOpen}
        jobs={selectedJobs}
        selectedDate={selectedDate}
        onClose={handleJobSelectionModalClose}
        onPostNewShift={handlePostNewShiftFromModal}
      />

      {/* Location Selector Modal */}
      <LocationSelectorModal
        isOpen={isLocationSelectorOpen}
        dateKey={selectedDate ? selectedDate.toISOString().split('T')[0] : undefined}
        locations={availableLocations}
        jobsPerLocationCount={jobsPerLocationCount}
        onSelect={handleLocationSelected}
        onClose={() => setIsLocationSelectorOpen(false)}
      />

      {/* Office Info Confirmation Modal */}
      <OfficeInfoConfirmModal
        isOpen={isOfficeInfoOpen}
        location={pendingOfficeLocation}
        onConfirm={handleOfficeInfoConfirm}
        onChange={handleOfficeInfoChange}
        onClose={() => setIsOfficeInfoOpen(false)}
      />

      {/* Terms of Service Modal */}
      <TermsModal
        isOpen={isTermsOpen}
        onAgree={async () => {
          try {
            const ts: any = (window as any).TempStars;
            const nowUtc = new Date().toISOString().slice(0, 19).replace('T', ' ');
            await ts?.Api?.updateDentist?.({ newTermsSeenOn: nowUtc });
            await ts?.User?.refresh?.();
          } catch (e) {
            // ignore
          }
          setIsTermsOpen(false);
        }}
      />

      {/* Job Board Policy Modal */}
      <JobBoardPolicyModal
        isOpen={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
        onAgree={async () => {
          setIsPolicyModalOpen(false);
          try {
            // Update dentist flag then navigate
            // Use legacy bridge where possible; fallback to Api if needed
            if ((window as any).TempStars?.Api?.updateDentist) {
              await (window as any).TempStars.Api.updateDentist({ isAcceptedJobBoardPolicy: true });
            } else if ((Api as any).updateUser) {
              await (Api as any).updateUser({ isAcceptedJobBoardPolicy: true });
            }
            // Refresh local user cache if available
            await (window as any).TempStars?.User?.refresh?.();
          } catch (e) {
            // ignore errors; still proceed
          }
          (window as any).TempStars?.App?.goReact('jobBoard');
        }}
      />
      
      <PhotoAnnounceModal
        open={!!announcement?.open}
        photoUrl={announcement?.photoUrl}
        linkUrl={announcement?.linkUrl}
        role="dentist"
        locationId={activeLocationId}
        onDismiss={() => setAnnouncement(null)}
      />

      <FixedLoader loading={isLoading} />
    </div>
    </>
  );
};

export default DentistHome;
